import type { AppContextAction } from '../components/AppContext';
import { useEffect, useRef, FunctionComponent, useState, useCallback, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { cap } from "../lib/cap";
import { compressToEncodedURIComponent } from 'lz-string';
import { Modal } from "../components/Modal";
import { useAppContext } from "../components/AppContext"
import { LinkButton } from "../components/LinkButton"
import Sortable from 'sortablejs';
import sampleQueries from "../utils/example-queries";
import 'sparnatural';
import 'leaflet';
import 'leaflet.markercluster';
// CSS
import "sparnatural/dist/sparnatural.css";
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import "./SparnaturalPage.css"
import { useLdo } from '@ldo/solid-react';
import { useWsContext } from '../components/WsContext';
import { Loading } from '../components/Loading';
import { makeNamedNode } from '../lib/nodes';
import { rdf, spoty } from '../lib/ns';
import { useNavigate } from 'react-router-dom';

type SparqlEngine = {
    populate: (quads: object[]) => void;
    executeSparql: (sparql: string, callback: (data: {}) => void, errorCallback: (error: any) => void) => void;
};

export const SparnaturalPage: FunctionComponent = () => {
    const { i18n, t } = useTranslation('spoty');
    const sparnaturalRef = useRef<HTMLElement>(null);
    const yasqeRef = useRef<any>(null);
    const yasrRef = useRef<any>(null);
    const [currentModal, setCurrentModal] = useState<null | "share" | "export" | "import">(null);
    const [showQueries, setShowQueries] = useState(false);
    const [exportJson, setExportJson] = useState<string>("");
    const [shareUrl, setShareUrl] = useState("");
    const [appCtx, dispatch] = useAppContext();
    const wsCtx = useWsContext();
    const [resetKey, setResetKey] = useState(0);
    const queries = appCtx.preferences.queries ?? sampleQueries;
    const { dataset } = useLdo();
    const [oxistoreReady, setOxistoreReady] = useState(false);
    const [sparqlEngine, setSparqlEngine] = useState<SparqlEngine>();
    const navigate = useNavigate();

    // create/destroy the oxigraph worker
    useEffect(() => {
        const worker = new Worker(new URL('../workers/oxigraph.worker.ts', import.meta.url));

        const pendingQueries = new Map<string, { callback: (data: {}) => void, errorCallback: (error: any) => void }>();
        let queryId = 0;

        worker.onmessage = (e: MessageEvent) => {
            const msg = e.data;
            if (msg.type === 'populated') {
                console.debug("oxigraph worker: store ready, size:", msg.size);
                setOxistoreReady(true);
            } else if (msg.type === 'result') {
                const pending = pendingQueries.get(msg.id);
                if (pending) {
                    pendingQueries.delete(msg.id);
                    document.body.style.cursor = "default";
                    pending.callback(msg.results);
                }
            } else if (msg.type === 'error') {
                const pending = pendingQueries.get(msg.id);
                if (pending) {
                    pendingQueries.delete(msg.id);
                    pending.errorCallback(new Error(msg.error));
                }
            }
        };

        setSparqlEngine({
            populate: (quads) => {
                worker.postMessage({ type: 'populate', quads });
            },
            executeSparql: (sparql, callback, errorCallback) => {
                console.debug("sparnatural...executeSparql");
                const id = String(++queryId);
                pendingQueries.set(id, { callback, errorCallback });
                worker.postMessage({ type: 'query', id, sparql });
            },
        });

        return () => {
            worker.terminate();
        };
    }, []);

    // populate the worker's store when all sentences are loaded and dataset changes
    useEffect(() => {
        if (wsCtx.isLoading() || !sparqlEngine) return;

        console.debug("effect: sending quads to oxigraph worker");
        setOxistoreReady(false);

        const quads = Array.from(dataset).map(q => ({
            subject: { termType: q.subject.termType, value: q.subject.value },
            predicate: { termType: q.predicate.termType, value: q.predicate.value },
            object: {
                termType: q.object.termType,
                value: q.object.value,
                language: 'language' in q.object ? (q.object as any).language : undefined,
                datatype: 'datatype' in q.object ? (q.object as any).datatype?.value : undefined,
            },
        }));

        sparqlEngine.populate(quads);
    }, [dataset, wsCtx, sparqlEngine]);

    const sparnatural_on_init = useCallback((event: any) => {
        console.debug("callback sparnatural_on_init");

        const sparnatural = sparnaturalRef.current as any;
        const yasr = yasrRef.current as any;
        // NB: this callback is only set when the two refs above are not null

        // notify the specification to yasR plugins
        for (const plugin in yasrRef.current.plugins) {
            if (yasr.plugins[plugin].notifyConfiguration) {
                yasr.plugins[plugin].notifyConfiguration(
                    event.detail.config
                );
            }
        }
        sparnatural.customization = {
            sparqlHandler: {
                executeSparql: sparqlEngine!.executeSparql,
            }
        }
    }, [sparqlEngine]);

    const sparnatural_on_queryUpdated = useCallback((event: any) => {
        console.debug("sparnatural::queryUpdated");

        const sparnatural = sparnaturalRef.current as any;
        // NB: this callback is only set when sparnaturalRef.current is not null

        // get the SPARQL query string, and pass it to yasQE
        const queryString = sparnatural.expandSparql(event.detail.queryString);
        yasqeRef.current.setValue(queryString);

        // save query
        const query = document.getElementById('query-json') as HTMLSelectElement;
        if (query) {
            query.value = JSON.stringify(event.detail.queryJson);
        }

        // notify the query to yasR plugins
        for (const plugin in yasrRef.current.plugins) {
            if (yasrRef.current.plugins[plugin].notifyQuery) {
                yasrRef.current.plugins[plugin].notifyQuery(event.detail.queryJson);
            }
        }
    }, []);

    const sparnatural_on_submit = useCallback((_event: any) => {
        console.debug("sparnatural::submit");

        const sparnatural = sparnaturalRef.current as any;
        // NB: this callback is only set when sparnaturalRef.current is not null

        sparnatural.disablePlayBtn();
        const query = yasqeRef.current.getValue();
        sparnatural.customization.sparqlHandler.executeSparql(
            query,
            (results: any) => {
                yasrRef.current.setResponse(results);
                sparnatural.enablePlayBtn();
            },
            (error: Error) => {
                console.error("Error executing SPARQL query:", error);
                sparnatural.enablePlayBtn();
            }
        );
    }, []);

    const toggleYasqe: MouseEventHandler = useCallback((event) => {
        const yasqeEl = document.getElementById("yasqe");
        if (!yasqeEl) {
            return false;
        }
        const isHidden = window.getComputedStyle(yasqeEl).display === 'none';
        yasqeEl.style.display = isHidden ? 'block' : 'none';

        const icon = (event.target as HTMLElement).querySelector('i');
        if (icon) {
            icon.className = isHidden ? 'fad fa-eye-slash fa-fw' : 'fad fa-eye fa-fw';
        }

        if (isHidden) {
            yasqeRef.current.refresh();
        }
    }, []);

    const yasqe_on_query = useCallback((yasqe: any, _req: any) => {
        console.debug("callback yasqe@query")

        const sparnatural = sparnaturalRef.current as any;
        // NB: this callback is only set when sparnaturalRef.current is not null

        sparnatural.disablePlayBtn();
        const query = yasqe.getValue();
        sparnatural.executeSparql(
            query,
            (response: Response, duration: number) => {
                yasrRef.current.setResponse(response, duration);
                sparnatural.enablePlayBtn();
            },
            (error: Error) => {
                console.error("Error executing SPARQL query:", error);
                sparnatural.enablePlayBtn();
            }
        );
    }, []);

    const yasr_on_click: MouseEventHandler<HTMLElement> = useCallback((event) => {
        if (!event.target) {
            return false;
        }
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
            return false; // do not override "modified" clicks
        }
        let target = event.target as HTMLElement;
        console.log("yasr_on_click", target);
        if (target.tagName === "A") {
            const href: string = (target as any).href;
            const wsUri = wsCtx.workspace.uri;
            const shortUri = (href.startsWith(wsUri))
              ? `./${href.substring(wsUri.length)}`
              : href;

            for (const q of dataset.match(makeNamedNode(href), makeNamedNode(rdf.type))) {
                switch (q.object.value) {
                    case spoty.Sentence:
                        navigate(wsCtx.route() + `s/${encodeURIComponent(shortUri)}/`);
                        event.preventDefault();
                        return false;
                    // TODO other recognized types (Simulus, Token)
                    // NB: Token will be tricky, as we need to find its sentence
                }
            }
            for (const q of dataset.match(null, makeNamedNode(spoty.language), makeNamedNode(href))) {
                navigate(wsCtx.route() + `lang/${encodeURIComponent(shortUri)}/`);
                event.preventDefault();
                return false;
            }
        }
    }, [dataset, navigate, wsCtx]);

    // initialize Sparnatural, YasQE and YasR
    useEffect(() => {(async () => {
        const sparnatural = sparnaturalRef.current as any;
        const yasqeElement = document.getElementById("yasqe");
        const yasrElement = document.getElementById("yasr");

        if (!sparnatural) {
            console.warn("sparnaturalRef is empty")
            return;
        }
        if (!yasqeElement || !yasrElement) {
            console.warn("sparnatural: yasgeElement or yasrElement not found");
            return;
        }
        if (!oxistoreReady) {
            console.info("oxistore not yet ready for sparnatural");
            return;
        }

        console.debug("effect initialize Sparnatural and Yasgui");
        yasqeElement.innerHTML = '';
        yasrElement.innerHTML = '';

        let Yasqe = (window as any).Yasqe;
        // init yasQE query editor
        yasqeRef.current = new Yasqe(yasqeElement, {
            copyEndpointOnNewTab: false,
        });
        yasqeRef.current.on("query", yasqe_on_query);

        let Yasr = (window as any).Yasr as any;
        let SparnaturalYasguiPlugins = (window as any).SparnaturalYasguiPlugins;
        // register a specific plugin that is capable of displaying clikable label + URI
        Yasr.registerPlugin("TableX", SparnaturalYasguiPlugins.TableX);
        Yasr.registerPlugin("Grid", SparnaturalYasguiPlugins.GridPlugin);
        Yasr.registerPlugin("Stats", SparnaturalYasguiPlugins.StatsPlugin);
        Yasr.registerPlugin("Map", SparnaturalYasguiPlugins.MapPlugin);
        delete Yasr.plugins['table'];
        // delete Yasr.plugins['response']; // TODO uncomment when 'sparnatural' is not flagged anymore as 'experimental'
        // init yasR result display
        yasrRef.current = new Yasr(yasrElement, {
            pluginOrder: ["TableX", "Grid", "Stats", "Map"],
            defaultPlugin: "TableX",
            persistencyExpire: 0,
            maxPersistentResponseSize: 0,
        });

        sparnatural.addEventListener("init", sparnatural_on_init);

        // listener when sparnatural updates the query
        // see http://docs.sparnatural.eu/Javascript-integration.html#sparnatural-events
        sparnatural.addEventListener("queryUpdated", sparnatural_on_queryUpdated);

        sparnatural.addEventListener("submit", sparnatural_on_submit);

        sparnatural.addEventListener("reset", (_event: any) => {
            yasqeRef.current.setValue("");
        });
    })()});

    const resetSparnaturalYasgui = useCallback(() => {
        setResetKey(prev => prev + 1);
    }, []);

    const importJson = () => {
        const importTextarea = document.getElementById("import-json") as HTMLTextAreaElement;
        if (!importTextarea.value) return;
        try {
            const json = JSON.parse(importTextarea.value);
            const sparnatural = sparnaturalRef.current as any;
            if (sparnatural) {
                sparnatural.loadQuery(json);
                setCurrentModal(null);
            }
        }
        catch (e) {
            console.error("JSON:", e);
            alert(t("There's a problem with the JSON."));
        }
    };

    useEffect(() => {
        if (currentModal === "share") {
            const query = document.getElementById('query-json') as HTMLInputElement;
            if (query) {
                const queryValue = query.value;
                const compressed = compressToEncodedURIComponent(queryValue);
                const url = `${window.location.origin}${window.location.pathname}?query=${compressed}`;
                setShareUrl(url);
            }
        };
    }, [currentModal]);

    const loadSelectedQuery = (query: string) => {
        const sparnatural = sparnaturalRef.current as any;
        if (sparnatural) {
            sparnatural.loadQuery(query);
        }
    }

    const exportButton = () => {
        setCurrentModal("export");
        const queryInput = document.getElementById('query-json') as HTMLInputElement;
        if (queryInput && queryInput.value) {
            const json = JSON.parse(queryInput.value);
            setExportJson(JSON.stringify(json, null, 2));
        }
    }

    const copyQueryFromExport = () => {
        if (!exportJson) return;
        navigator.clipboard.writeText(exportJson);
        alert(t("Query copied!"));
    }

    const saveQueryFromExport = () => {
        if (!exportJson) return;
        const queryName = prompt(t("Enter a name for this new query:"));

        if (!queryName) {
            alert(cap(t("failed to save")) + ", " + t("you must enter a name") + ".");
            return;
        }

        if (queries.hasOwnProperty(queryName)) {
            alert(cap(t("failed to save")) + ", " + t("query name already exists") + ".");
            return;
        }

        const updatedQueries = { ...queries, [queryName]: JSON.parse(exportJson) };

        dispatch({
            type: "setQueries",
            value: updatedQueries,
        });

        resetSparnaturalYasgui();

        alert(t("Query saved!"));
    }

    return (
      !oxistoreReady
      ? <Loading>{t("Sparnatural needs all sentences to be completely loaded...")}</Loading>
      : <>
          <div id="queries">
                <SelectSavedQueries queries={queries} onSelectQuery={loadSelectedQuery} />
                <LinkButton className="buttonQueries" onClick={() => setShowQueries(true)}>{cap(t("modify my queries"))}</LinkButton>
                <Modal show={showQueries} setShow={setShowQueries}>
                    <ManageSavedQueries queries={queries} dispatch={dispatch} showQueries={showQueries} onQueriesUpdated={resetSparnaturalYasgui} />
                    <NewQueryForm queries={queries} dispatch={dispatch} onQueriesUpdated={resetSparnaturalYasgui} />
                </Modal>
            </div>
                <div key={resetKey}>
                    <section id="sparnatural-section">
                        <div id="ui-search">
                            <spar-natural
                                ref={sparnaturalRef}
                                src={process.env.PUBLIC_URL + "/assets/sparnatural/config.ttl"}
                                lang={i18n.language}
                                defaultLang="en"
                                endpoint="https://dbpedia.org/sparql" // not actually used, but required
                                distinct="true"
                                limit="500"
                                prefix=":https://w3id.org/SpOTy/ontology# skos:http://www.w3.org/2004/02/skos/core#"
                                debug="false"
                            />
                            <input type="hidden" id="query-json"></input>
                        </div>
                        <div id="buttonContainer">
                            <button id="sparql-toggle" onClick={toggleYasqe}><i className="fad fa-eye-slash fa-fw"></i>&nbsp;{cap(t("toggle SPARQL query"))}</button>
                            &nbsp;|&nbsp;
                            <button id="share" onClick={() => setCurrentModal("share")}><i className="fad fa-share-square"></i>&nbsp;{cap(t("share"))}</button>
                            &nbsp;|&nbsp;
                            <button id="export" onClick={exportButton}><i className="fad fa-file-export"></i>&nbsp;{cap(t("export query in JSON"))}</button>
                            &nbsp;|&nbsp;
                            <button id="import" onClick={() => setCurrentModal("import")}><i className="fad fa-file-import"></i>&nbsp;{cap(t("import query in JSON"))}</button>
                        </div>
                    </section>

                    < section id="yasqe-section" >
                        <div id="yasqe"></div>
                    </section >

                    <section id="yasr-section" onClick={yasr_on_click}>
                        <div id="yasr"></div>
                    </section>
                </div>

            <Modal show={currentModal !== null} setShow={() => setCurrentModal(null)}>
                {currentModal === "share" && (
                    <>
                        <h2>{cap(t("direct query link"))}</h2>
                        <div className="linkBreak">
                            <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                                {shareUrl}
                            </a>
                        </div>
                    </>
                )}

                {currentModal === "export" && (
                    <>
                        <h2>{cap(t("query as JSON"))}</h2>
                        <div id="exportQuery">
                            <textarea readOnly value={exportJson} className="modalTextarea"></textarea>
                            <button onClick={copyQueryFromExport}>{cap(t("copy"))}</button>
                            <button onClick={saveQueryFromExport}>{cap(t("save"))}</button>
                        </div>
                    </>
                )}

                {currentModal === "import" && (
                    <>
                        <h2>{cap(t("query as JSON"))}</h2>
                        <div id="importQuery">
                            <textarea id="import-json"></textarea>
                            <button onClick={importJson}>{cap(t("import"))}</button>
                        </div>
                    </>
                )}
            </Modal>
        </>
    );
};

const SelectSavedQueries: FunctionComponent<{
    queries: Record<string, string>,
    onSelectQuery: (query: string) => void
}> = ({
    queries,
    onSelectQuery
}) => {
        const { t } = useTranslation('spoty');
        const [selectedKey, setSelectedKey] = useState("none");

        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
            const key = e.target.value;
            setSelectedKey(key);
            if (key !== "none" && queries.hasOwnProperty(key)) {
                onSelectQuery(queries[key]);
            }
        };

        return (<select id="select-examples" value={selectedKey} onChange={handleChange}>
            <option value="none">{cap(t("select a query..."))}</option>
            {Object.entries(queries).map(([key]) => (
                <option key={key} value={key}>
                    {cap(t(key))}
                </option>
            ))}
        </select>
        );
    };

const ManageSavedQueries: FunctionComponent<{
    queries: Record<string, string>,
    dispatch: React.Dispatch<AppContextAction>,
    showQueries: boolean,
    onQueriesUpdated: () => void
}> = ({
    queries,
    dispatch,
    showQueries,
    onQueriesUpdated
}) => {
        const { t } = useTranslation('spoty');

        const deleteQuery = (label: string) => {
            if (window.confirm(t("Confirm deleting \"{{label}}\"?", { label }))) {
                const updatedQueries = { ...queries };
                delete updatedQueries[label];

                dispatch({
                    type: "setQueries",
                    value: updatedQueries,
                })
                onQueriesUpdated();
            }
        };

        const renameQuery = (label: string) => {
            const newName = prompt(t("Enter the new name:"), label);
            if (!newName || newName === label) return;
            if (queries.hasOwnProperty(newName)) {
                alert(cap(t("query name already exists")) + ".");
                return;
            }

            const updatedQueries = { ...queries };
            updatedQueries[newName] = updatedQueries[label];
            delete updatedQueries[label];

            dispatch({
                type: "setQueries",
                value: updatedQueries,
            });
            onQueriesUpdated();
        };

        // function to reorder the list of queries
        const listRef = useRef<HTMLOListElement>(null);
        const sortableRef = useRef<Sortable | null>(null);

        useEffect(() => {
            if (!showQueries) return;

            const timeout = setTimeout(() => {
                if (!listRef.current) return;

                sortableRef.current = Sortable.create(listRef.current, {
                    handle: '.handle',
                    animation: 150,
                    ghostClass: 'sortable-ghost',
                    onEnd: (evt) => {
                        if (evt.oldIndex === undefined || evt.newIndex === undefined) return;

                        const entries = Object.entries(queries);

                        const newOrder = [...entries];
                        const [moved] = newOrder.splice(evt.oldIndex, 1);
                        newOrder.splice(evt.newIndex, 0, moved);

                        const updatedQueries: typeof queries = {};
                        newOrder.forEach(([key, value]) => {
                            updatedQueries[key] = value;
                        });

                        dispatch({
                            type: "setQueries",
                            value: updatedQueries
                        });
                        onQueriesUpdated()
                    }
                });

            }, 50);

            return () => {
                clearTimeout(timeout);
                if (sortableRef.current) {
                    sortableRef.current.destroy();
                    sortableRef.current = null;
                }
            };
        }, [showQueries, queries, dispatch, onQueriesUpdated]);

        return (<>
            <h2>{cap(t("my queries"))}</h2>
            <ol ref={listRef}>
                {Object.entries(queries).map(([key]) => (
                    <li className="liQuery" key={key}>
                        {cap(t(key))}
                        <LinkButton help={t("rename")} onClick={() => renameQuery(key)} className="tools">✏️</LinkButton>
                        <LinkButton help={t("delete")} onClick={() => deleteQuery(key)} className="tools">🗑️</LinkButton>
                        <span className='handle' title={t("move")}>☰</span>
                    </li>
                ))}
            </ol>
        </>
        );
    };

const NewQueryForm: FunctionComponent<{
    queries: Record<string, string>,
    dispatch: React.Dispatch<AppContextAction>,
    onQueriesUpdated: () => void
}> = ({
    queries,
    dispatch,
    onQueriesUpdated
}) => {

        const { t } = useTranslation('spoty');
        const [nameNewQuery, setNameNewQuery] = useState("");
        const [textNewQuery, setTextNewQuery] = useState("");
        const [queryStatus, setQueryStatus] = useState<{ message: string; color: string } | null>(null);

        const cancel = () => {
            setNameNewQuery("");
            setTextNewQuery("");
        }

        const saveQuery = (nameQuery: string, textQuery: string) => {
            try {
                if (queries.hasOwnProperty(nameQuery)) {
                    setQueryStatus({ message: cap(t("query name already exists") + "."), color: "red" });
                    setTimeout(() => {
                        setQueryStatus(null);
                    }, 3000);
                    return;
                }

                const updatedQueries = { ...queries, [nameQuery]: JSON.parse(textQuery) };

                dispatch({
                    type: "setQueries",
                    value: updatedQueries,
                });

                onQueriesUpdated();

                cancel();
                const newQuery = document.getElementById("newQuery");
                if (newQuery) newQuery.style.display = "none";
                setQueryStatus({ message: t("Query saved!"), color: "green" });
                setTimeout(() => {
                    setQueryStatus(null);
                }, 2000);
            }
            catch (e) {
                setQueryStatus({ message: t("There is an error."), color: "red" });
                setTimeout(() => {
                    setQueryStatus(null);
                }, 3000);
            }
        }

        const addQuery = () => {
            const newQuery = document.getElementById("newQuery");
            if (newQuery) {
                const isHidden = window.getComputedStyle(newQuery).display === 'none';
                newQuery.style.display = isHidden ? "flex" : "none";
            }
        }

        return (
            <>
                <h3 id="titleAddNewQuery">{cap(t("add a new query"))}
                    <LinkButton help={t("add")} onClick={addQuery} className="tools">➕</LinkButton>
                </h3>
                <div id="newQuery">
                    <label htmlFor="nameNewQuery">{t("Name of the query:")}</label>
                    <input value={nameNewQuery} id="nameNewQuery" type="text" onChange={e => setNameNewQuery(e.target.value)}></input>
                    <label htmlFor="textNewQuery">{t("The query in JSON:")}</label>
                    <textarea value={textNewQuery} id="textNewQuery" onChange={e => setTextNewQuery(e.target.value)}></textarea>
                    <div id="buttonsNewQuery">
                        <button id="cancelButton" onClick={cancel}>{cap(t("cancel"))}</button>
                        <button onClick={() => saveQuery(nameNewQuery, textNewQuery)}>{cap(t("save"))}</button>
                    </div>
                </div>
                <p id="queryStatus" style={{ color: queryStatus?.color }}>{queryStatus?.message}</p>
            </>
        );
    };

